import { supabase } from './supabase';
import { createFuzzyMatcher } from '../utils/fuzzyMatch';
import { ContextAnalyzer } from '../utils/contextAnalyzer';
import { StyleTransformer } from '../utils/styleTransformer';


export class ChatbotService {
  constructor() {
    // Lower the threshold for fuzzy matching
    this.fuzzyMatcher = createFuzzyMatcher(0.6); // Changed from 0.8
    this.contextAnalyzer = new ContextAnalyzer();
    this.styleTransformer = new StyleTransformer();
  }

  async getResponse(userMessage) {
    console.log('1. Starting getResponse with:', userMessage);
    
    try {
      // Log Supabase connection
      console.log('2. Supabase client:', supabase);
      
      const { data: patterns, error } = await supabase
        .from('chatbot_responses')
        .select('*')
        .eq('is_active', true)
        .order('priority', { ascending: false });
      
      console.log('3. Patterns from database:', patterns);
      console.log('3a. Any errors:', error);

      if (error) {
        console.error('4. Supabase error:', error);
        return this.handleError();
      }

      const context = this.contextAnalyzer.analyzeInput(normalizedInput);
      console.log('Context analysis:', context);

      // Try to find best matching pattern
      const bestMatch = this.findBestMatch(normalizedInput, patterns, context);
      console.log('Best match found:', bestMatch);
      
      if (bestMatch) {
        const styledResponse = this.styleTransformer.transform(
          bestMatch.response, 
          context
        );

        this.contextAnalyzer.updateContext(normalizedInput, styledResponse);

        return {
          text: styledResponse,
          sender: 'bot',
          confidence: bestMatch.confidence
        };
      }

      // Simple matching as fallback
      const simpleMatch = patterns.find(pattern => 
        pattern.examples.some(example => {
          const normalizedExample = this.normalizeInput(example);
          console.log('Comparing:', normalizedExample, 'with:', normalizedInput);
          return normalizedExample.includes(normalizedInput) || 
                 normalizedInput.includes(normalizedExample);
        })
      );

      if (simpleMatch) {
        console.log('Found simple match:', simpleMatch);
        const styledResponse = this.styleTransformer.transform(
          simpleMatch.response,
          context
        );
        return {
          text: styledResponse,
          sender: 'bot',
          confidence: 0.7
        };
      }

      return this.handleNoMatch(context);

    } catch (error) {
      console.error('X. Caught error:', error);
      return this.handleError();
    }
}



  findBestMatch(input, patterns, context) {
    let bestMatch = null;
    let highestScore = 0;
    console.log('Finding best match for:', input);

    for (const pattern of patterns) {
      for (const example of pattern.examples) {
        const similarityScore = this.calculateSimilarityScore(input, example);
        const contextScore = this.calculateContextScore(pattern, context);
        const totalScore = similarityScore + contextScore;
        
        console.log('Pattern:', pattern.intent);
        console.log('Example:', example);
        console.log('Scores:', { similarityScore, contextScore, totalScore });

        if (totalScore > highestScore) {
          highestScore = totalScore;
          bestMatch = {
            ...pattern,
            confidence: totalScore
          };
        }
      }
    }

    return highestScore > 0.4 ? bestMatch : null; // Lowered threshold from 0.6
  }

  calculateSimilarityScore(input, example) {
    // Base similarity using fuzzy matching
    const normalizedExample = this.normalizeInput(example);
    return this.fuzzyMatcher(input, normalizedExample) ? 0.8 : 0;
  }

  calculateContextScore(pattern, context) {
    let score = 0;

    // Add score for matching topics
    if (pattern.parameters && context.topics) {
      const matchingTopics = pattern.parameters.filter(param => 
        context.topics.includes(param)
      );
      score += matchingTopics.length * 0.1;
    }

    // Add score for question/statement matching
    if (pattern.isQuestion === context.isQuestion) {
      score += 0.1;
    }

    return score;
  }

  handleNoMatch(context) {
    // Generate contextual fallback based on analyzed context
    const fallback = this.styleTransformer.transform(
      "My cosmic sensors are having trouble understanding that query. Could you rephrase it?",
      context
    );

    return {
      text: fallback,
      sender: 'bot',
      confidence: 0
    };
  }

  handleError() {
    return {
      text: "My interdimensional connection seems disrupted. Let's try again? *cosmic static crackles* ✨",
      sender: 'bot'
    };
  }
}